/* eslint-disable react/no-array-index-key */
/* eslint-disable no-nested-ternary */
/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable jsx-a11y/no-noninteractive-tabindex */
import * as React from "react";
import { useSelect, UseSelectStateChange } from "downshift";
import IconChevronStepper from "./IconChevronStepper";
import styled, { CSSObject } from "styled-components";
import { ActionList, ActionListItem } from "components/ActionList";
import {
  Box,
  Card,
  FormLabel,
  Stack,
  Text,
  theme,
  UnstyledButton,
} from "@aksara-ui/react";
import { transparentize } from "polished";

export interface InputSelectProps<T> {
  /** The input select label */
  label?: string;
  /** Placeholder text for select label */
  placeholder?: string;
  /** Label items. */
  items: T[];
  /** Selected item. */
  selectedItem?: T;

  initialSelectedItem?: T | null;
  /** If the item list is an object/shape, use this to map it into string as list option item. */
  itemToString?: (item: T | null) => string;
  /** If the item list is an object/shape, use this to map it into string as value item. */
  itemValue?: (item: T | null) => string;
  /** The change handler for the select. */
  handleSelectedItemChange?: (changes: UseSelectStateChange<T>) => void;
  /** If the item list is an object/shape, use this to map a custom element to render on the UI. */
  itemRenderer?: (item: T) => React.ReactNode;
  /**  use this customize element to render input value. */
  inputValueRenderer?: (item: T) => React.ReactNode;
  /** Name of the field form */
  name?: string;
  /** Logic on focus */
  onFocus?: () => void;
  /** Logic on blue */
  onBlur?: () => void;

  disabled?: boolean;

  size?: "md" | "lg";

  errors?: boolean;

  width?: string | number;
  /** Max height for list box */
  maxHeight?: string | number;

  /** input text style */
  inputStyle?: CSSObject;

  paddingLeft? : string;

  borderRadius? : string;
}

const InputSelectStyled = styled(UnstyledButton)<{
  size?: string;
  variant?: string;
  paddingLeft? : string;
  borderRadius? : string;
}>`
  display: flex;
  align-items: center;
  position: relative;
  width: 100%;
  padding-left: ${(props) =>
    props.paddingLeft ? props.paddingLeft : "12px"};
  padding-right: 12px;
  background-color: ${theme.colors.grey01};
  border: 1px solid;
  border-color: ${theme.colors.grey04};
  border-radius: ${(props) =>
    props.borderRadius ? props.borderRadius : "8px"};
  outline: none;
  height: ${(props) =>
    props.size === "md" ? "32px" : props.size === "lg" ? "40px" : ""};

  &[disabled] {
    background-color: #fafbfd;
    border: 1px solid;
    border-color: ${transparentize(0.5, "#C6D0DB")};
    color: #b0b7bf;
    cursor: not-allowed;
  }

  ${(props) => {
    if (props.variant === "default") {
      return `
        &:not([disabled]) {
            &:focus, &:active {
                outline: none;
                border-color: ${theme.colors.blue06};
                box-shadow: 0 0 2px ${transparentize(0.7, theme.colors.blue03)};
                &:hover {
                    background-color: #FFFFFF;
                    border-color: ${theme.colors.blue06};
                    box-shadow: 0 0 0 2px ${transparentize(
                      0.7,
                      theme.colors.blue03
                    )};
                }
            }
            &:hover {
                border-color: #B0B7BF;
                background-color: #F5F6F8;
            }
        }
        `;
    }

    if (props.variant === "active") {
      return `
            &:not([disabled]) {
                outline: none;
                border-color: ${theme.colors.blue06};
                box-shadow: 0 0 0 2px ${transparentize(
                  0.7,
                  theme.colors.blue03
                )};
            }
        `;
    }

    if (props.variant === "error") {
      return `
            &:not([disabled]) {
                background-color: ${theme.colors.red01};
                border-color: ${theme.colors.red07};
            }
        `;
    }
  }}
`;

/** Base wrapper for dropdown selector element using Downshift.js */
function InputSelect<T>({
  label,
  placeholder = "Select an item",
  items,
  selectedItem,
  itemToString = (item) => (item ? String(item) : ""),
  itemValue = (item) => (item ? String(item) : ""),
  handleSelectedItemChange,
  inputValueRenderer,
  itemRenderer,
  initialSelectedItem,
  onBlur,
  onFocus,
  disabled,
  errors,
  size = "md",
  width = "100%",
  maxHeight,
  inputStyle,
  paddingLeft,
  borderRadius
}: InputSelectProps<T>) {
  const {
    isOpen,
    getToggleButtonProps,
    getLabelProps,
    getMenuProps,
    highlightedIndex,
    getItemProps,
  } = useSelect<T>({
    items,
    itemToString,
    selectedItem,
    onSelectedItemChange: handleSelectedItemChange,
    initialSelectedItem,
  });

  const renderInputValue = () => {
    if (!selectedItem) {
      return (
        <Text scale={200} color={!disabled ? "#313F4E" : "#B0B7BF"}>
          {placeholder}
        </Text>
      );
    }

    if (inputValueRenderer) {
      return inputValueRenderer(selectedItem);
    }

    return (
      <Text scale={200} color={!disabled ? "#313F4E" : "#B0B7BF"}>
        {itemToString ? itemToString(selectedItem) : selectedItem}
      </Text>
    );
  };
  return (
    <Box width={width}>
      <Stack spacing="xxs" display="block" position="relative">
        {label && (
          <FormLabel display="block" {...getLabelProps()}>
            {label}
          </FormLabel>
        )}
        <InputSelectStyled
          type="button"
          disabled={disabled}
          size={size}
          variant={errors ? "error" : isOpen ? "active" : "default"}
          paddingLeft={paddingLeft}
          borderRadius={borderRadius}
          onFocus={() => {
            if (onFocus) {
              onFocus();
            }
          }}
          onBlur={() => {
            if (onBlur) {
              onBlur();
            }
          }}
          {...getToggleButtonProps()}
        >
          {renderInputValue()}

          <Box position="absolute" right={8}>
            <IconChevronStepper size={16} />
          </Box>
        </InputSelectStyled>
        <Card
          position="absolute"
          float="left"
          top="100%"
          left={0}
          width={width}
          maxHeight={maxHeight}
          display={isOpen ? "block" : "none"}
          elevation={2}
          overflow="hidden"
          zIndex={10}
        >
          <ActionList
            px="sm"
            overflowY="auto"
            maxHeight={maxHeight}
            {...getMenuProps()}
          >
            {items.length !== 0 ? (
              items.map((item, index) => {
                const selected =
                  selectedItem && itemValue(selectedItem) === itemValue(item);
                return (
                  <ActionListItem
                    containerStyle={
                      highlightedIndex === index && (!selectedItem || !selected)
                        ? { backgroundColor: "#F5F6F8", borderRadius: "lg" }
                        : {}
                    }
                    isActive={selected}
                    key={`${itemToString(item)}`}
                    {...getItemProps({ item, index })}
                  >
                    {itemRenderer
                      ? itemRenderer(item)
                      : itemToString
                      ? itemToString(item)
                      : item}
                  </ActionListItem>
                );
              })
            ) : (
              <ActionListItem disabled>No items.</ActionListItem>
            )}
          </ActionList>
        </Card>
      </Stack>
      {/* if you Tab from menu, focus goes on button, and it shouldn't. only happens here. */}
      <div tabIndex={0} />
    </Box>
  );
}

export default InputSelect;
